import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  message, 
  Card, 
  Row, 
  Col,
  Tag,
  Typography,
  Drawer,
  List,
  Avatar,
  Select,
  DatePicker,
  Input,
  Form,
  Radio,
  Modal
} from 'antd';
import { 
  EyeOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  WechatOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../../utils/axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import ImageViewer from '../../components/ImageViewer';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 10;
// split 使用带 g 的正则；test 必须使用不带 g 的正则，否则 lastIndex 累积会导致匹配时灵时不灵
const URL_REGEX_GLOBAL = /(https?:\/\/[^\s]+|\/api\/upload\/download\/[A-Za-z0-9\-]+)/g;
const URL_REGEX_TEST = /(https?:\/\/[^\s]+|\/api\/upload\/download\/[A-Za-z0-9\-]+)/;

const FollowUpRecords = () => {
  const { user } = useAuth();
  const [followUps, setFollowUps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [leadFollowUps, setLeadFollowUps] = useState([]);
  const [leadAttachments, setLeadAttachments] = useState([]);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });

  const isAdminUser = user?.role === 'super_admin' || user?.role === 'admin';

  // 文本筛选输入即时态
  const [textFilters, setTextFilters] = useState({ search: '', lead_name: '', created_by: '' });
  // 实际生效的查询参数（文本输入经 400ms 防抖后同步，并做服务端分页）
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    lead_name: '',
    created_by: '',
    date_range: null,
    view_mode: 'my' // 'my' 表示只看自己的，'all' 表示看所有的
  });
  // 请求序号，用于丢弃过期响应、避免快速筛选时的竞态覆盖
  const reqSeqRef = useRef(0);

  // 打开图片预览
  const openImageViewer = (imageUrl, imageName, downloadUrl) => {
    if (imageUrl) {
      setCurrentImage({ 
        url: imageUrl, 
        name: imageName || '图片', 
        downloadUrl: downloadUrl || imageUrl 
      });
      setImageViewerVisible(true);
    }
  };

  // 关闭图片预览
  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };

  // 获取跟进记录列表（服务端分页）
  const fetchFollowUps = useCallback(async () => {
    const seq = ++reqSeqRef.current;
    setLoading(true);
    try {
      const params = {
        page: queryParams.page,
        limit: queryParams.limit
      };
      if (queryParams.search) params.search = queryParams.search;
      if (queryParams.lead_name) params.lead_name = queryParams.lead_name;
      if (queryParams.created_by) params.created_by = queryParams.created_by;
      if (queryParams.date_range && queryParams.date_range.length === 2) {
        params.start_date = queryParams.date_range[0].format('YYYY-MM-DD');
        params.end_date = queryParams.date_range[1].format('YYYY-MM-DD');
      }

      // 非管理员或「只看我的」模式下仅查询自己的记录
      if (queryParams.view_mode === 'my' || !isAdminUser) {
        params.created_by_id = user.id;
      }

      const response = await api.get('/follow-ups', { params });
      if (seq !== reqSeqRef.current) return; // 丢弃过期响应，避免快速筛选时竞态覆盖
      setFollowUps(response.data?.data || []);
      setTotal(response.data?.pagination?.total || 0);
    } catch (error) {
      if (seq !== reqSeqRef.current) return;
      console.error('获取跟进记录失败:', error);
      message.error('获取跟进记录失败');
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, [queryParams, isAdminUser, user?.id]);

  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps]);

  // 文本筛选输入 400ms 防抖；值未变化时保持原引用，避免多余请求
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = {
        search: textFilters.search.trim(),
        lead_name: textFilters.lead_name.trim(),
        created_by: textFilters.created_by.trim()
      };
      setQueryParams(prev => {
        if (prev.search === next.search &&
            prev.lead_name === next.lead_name &&
            prev.created_by === next.created_by &&
            prev.page === 1) return prev;
        return { ...prev, ...next, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [textFilters.search, textFilters.lead_name, textFilters.created_by]);

  // 处理文本筛选输入（经防抖生效）
  const handleTextFilterChange = (key, value) => {
    setTextFilters(prev => ({ ...prev, [key]: value }));
  };

  // 处理立即生效的筛选变化（日期、查看范围），并回到第一页
  const handleImmediateFilterChange = (key, value) => {
    setQueryParams(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // 清除所有筛选
  const handleClearAllFilters = () => {
    setTextFilters({ search: '', lead_name: '', created_by: '' });
    setQueryParams(prev => ({
      ...prev,
      page: 1,
      search: '',
      lead_name: '',
      created_by: '',
      date_range: null,
      view_mode: 'my'
    }));
  };

  // 查看线索详情
  const handleViewLead = async (record) => {
    setDrawerVisible(true);
    try {
      // 获取完整的线索详情
      const leadResponse = await api.get(`/leads/${record.lead_id}`);
      setCurrentLead(leadResponse.data);

      // 获取跟进历史
      const followUpResponse = await api.get(`/leads/${record.lead_id}/follow-ups`);
      setLeadFollowUps(followUpResponse.data || []);

      // 获取附件
      try {
        const attRes = await api.get(`/upload/lead/${record.lead_id}`);
        setLeadAttachments(attRes.data?.data || []);
      } catch (e) {
        console.warn('获取附件失败:', e);
        setLeadAttachments([]);
      }
    } catch (error) {
      console.error('获取线索详情失败:', error);
      setCurrentLead(record.lead); // 降级到使用表格中的数据
      setLeadFollowUps([]);
      setLeadAttachments([]);
    }
  };

  // 复制联系方式
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${type}已复制到剪贴板`);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 获取状态中文显示
  const getStatusText = (status) => {
    const statusMap = {
      'assigned': '已认领',
      'non_employee': '非员工',
      'pending': '待面试',
      'interviewed': '已面试',
      'offered': '已发offer',
      'hired': '已入职',
      'rejected': '已拒绝'
    };
    return statusMap[status] || status || '-';
  };

  // 获取跟进类型图标和标题
  const getFollowUpType = (content) => {
    if (content.includes('创建面试工单')) {
      return { icon: <CalendarOutlined style={{ color: '#52c41a' }} />, title: '面试工单' };
    } else if (content.includes('面试状态更新')) {
      return { icon: <CalendarOutlined style={{ color: '#52c41a' }} />, title: '面试状态更新' };
    } else if (content.includes('创建入职工单')) {
      return { icon: <FileTextOutlined style={{ color: '#722ed1' }} />, title: '入职管理' };
    } else if (content.includes('员工正式入职')) {
      return { icon: <TeamOutlined style={{ color: '#13c2c2' }} />, title: '员工入职' };
    } else {
      return { icon: <UserOutlined style={{ color: '#1890ff' }} />, title: '线索跟进' };
    }
  };

  // 跟进内容渲染（识别并预览图片链接）
  const renderFollowUpContent = (text) => {
    if (!text) return null;
    const tokens = text.split(URL_REGEX_GLOBAL);
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {tokens.map((tok, idx) => {
          if (!tok) return null;
          if (URL_REGEX_TEST.test(tok)) {
            const img = (
              <img
                src={tok}
                loading="lazy"
                alt="follow-up-img"
                style={{ width: '30%', maxWidth: 240, borderRadius: 6, marginTop: 6, display: 'block', cursor: 'zoom-in' }}
                onClick={() => Modal.info({
                  title: '图片预览',
                  content: (
                    <div style={{ textAlign: 'center' }}>
                      <img src={tok} alt="preview" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                    </div>
                  ),
                  width: 800,
                })}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            );
            return (
              <span key={idx} style={{ display: 'inline-block', maxWidth: '100%' }}>
                <a href={tok} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{tok}</a>
                {img}
              </span>
            );
          }
          return <span key={idx}>{tok}</span>;
        })}
      </div>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '线索信息',
      key: 'lead',
      width: 200,
      render: (_, record) => (
        <div style={{ cursor: 'pointer' }} onClick={() => handleViewLead(record)}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{record.lead?.name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <PhoneOutlined /> {record.lead?.phone}
          </div>
        </div>
      )
    },
    {
      title: '跟进类型',
      key: 'type',
      width: 120,
      render: (_, record) => {
        const { icon, title } = getFollowUpType(record.content);
        return (
          <Space>
            {icon}
            {title}
          </Space>
        );
      }
    },
    {
      title: '跟进内容',
      key: 'content',
      render: (_, record) => (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {renderFollowUpContent(record.content)}
        </div>
      )
    },
    {
      title: '跟进人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (_, record) => {
        const name = record.created_by_name ?? (record.created_by == null ? '系统' : '未知用户');
        return (<span style={{ whiteSpace: 'nowrap' }}>{name}</span>);
      }
    },
    {
      title: '跟进时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (text) => (
        <span style={{ whiteSpace: 'nowrap' }}>{dayjs(text).format('YYYY-MM-DD HH:mm')}</span>
      )
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewLead(record)}
            size="small"
            title="查看线索详情"
          >
            查看
          </Button>
        </div>
      )
    }
  ];

  return (
    <main className="ant-layout-content css-l9pxc0">
      <div style={{ padding: '10px 16px' }}>
      {/* 筛选区域 */}
      <Card 
        style={{ marginBottom: '16px' }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>筛选条件</span>
            <Button 
              type="text" 
              onClick={handleClearAllFilters}
              size="small"
              style={{ padding: 0, fontSize: '12px' }}
            >
              清除筛选
            </Button>
          </div>
        }
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="搜索">
              <Input
                placeholder="搜索跟进内容"
                value={textFilters.search}
                onChange={(e) => handleTextFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
                style={{ width: '100%' }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="线索姓名">
              <Input
                placeholder="搜索线索姓名"
                value={textFilters.lead_name}
                onChange={(e) => handleTextFilterChange('lead_name', e.target.value)}
                style={{ width: '100%' }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="跟进人">
              <Input
                placeholder="搜索跟进人"
                value={textFilters.created_by}
                onChange={(e) => handleTextFilterChange('created_by', e.target.value)}
                style={{ width: '100%' }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="跟进时间">
              <RangePicker
                value={queryParams.date_range}
                onChange={(dates) => handleImmediateFilterChange('date_range', dates)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 跟进记录列表 */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>跟进记录列表</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isAdminUser && (
                <Radio.Group
                  value={queryParams.view_mode}
                  onChange={(e) => handleImmediateFilterChange('view_mode', e.target.value)}
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="my">只看我的</Radio.Button>
                  <Radio.Button value="all">查看所有</Radio.Button>
                </Radio.Group>
              )}
              <Button
                type="text"
                icon={<ReloadOutlined />}
                size="small"
                onClick={fetchFollowUps}
                title="刷新数据"
              >
                刷新
              </Button>
            </div>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={followUps}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: queryParams.page,
            pageSize: queryParams.limit,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (t, range) => `第 ${range[0]}-${range[1]} 条，共 ${t} 条`,
            onChange: (page, pageSize) => setQueryParams(prev => ({
              ...prev,
              // 切换每页条数时回到第一页，翻页时使用目标页码
              page: prev.limit === pageSize ? page : 1,
              limit: pageSize
            }))
          }}
        />
      </Card>

      {/* 线索详情抽屉 */}
      <Drawer
        title="线索详情与跟进历史"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={800}
      >
        {currentLead && (
          <div>
            {/* 基本信息 */}
            <Card title="基本信息" size="small" style={{ marginBottom: '16px' }}>
              <Row gutter={[16, 4]}>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>姓名：</strong>{currentLead.name}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>手机：</strong>
                  <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => copyToClipboard(currentLead.phone, '手机号')}>
                    {currentLead.phone}
                  </span>
                </div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>邮箱：</strong>{currentLead.email || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>微信：</strong>{currentLead.wechat ? (
                  <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => copyToClipboard(currentLead.wechat, '微信号')}>
                    {currentLead.wechat}
                  </span>
                ) : '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>性别：</strong>{currentLead.gender === 'male' ? '男' : currentLead.gender === 'female' ? '女' : '未知'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>年龄：</strong>{currentLead.age || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>学历：</strong>{currentLead.education || currentLead.education_level || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>期望职位：</strong>{currentLead.expected_position || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>期望薪资：</strong>{currentLead.expected_salary || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>来源：</strong>{currentLead.source_name || currentLead.source || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>状态：</strong>{getStatusText(currentLead.status)}</div></Col>
                <Col span={24}><div style={{ lineHeight: 1.35, marginBottom: 2 }}><strong>备注：</strong>{currentLead.notes || '-'}</div></Col>
              </Row>
              {/* 附件展示 */}
              <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 10, paddingTop: 10 }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>附件</Title>
                {leadAttachments.length === 0 ? (
                  <Text type="secondary">暂无附件</Text>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                    {leadAttachments.map((f) => {
                      const isImg = (f.mimetype && f.mimetype.startsWith('image/')) || f.isImage || (f.url && /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.url));
                      return (
                        <div key={f.id} style={{ textAlign: 'center' }}>
                          {isImg ? (
                            <img 
                              src={f.url} 
                              alt={f.originalName}
                              loading="lazy"
                              style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                              onClick={() => openImageViewer(f.url, f.originalName, f.url)}
                            />
                          ) : (
                            <div style={{ width: 96, height: 96, border: '1px solid #eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                              <FileTextOutlined style={{ fontSize: 24, color: '#999' }} />
                            </div>
                          )}
                          <div style={{ marginTop: 4, fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.originalName}>{f.originalName}</div>
                          <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>查看/下载</a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>

            {/* 跟进历史 */}
            <Card title="跟进历史" size="small">
              <List
                dataSource={leadFollowUps}
                renderItem={(item) => {
                  const { icon, title } = getFollowUpType(item.content);
                  return (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={icon} />}
                        title={`${title} - ${item.created_by_name || item.created_by_username || '系统'} - ${dayjs(item.created_at).format('YYYY-MM-DD HH:mm')}`}
                        description={
                          <div style={{ 
                            background: '#f8f9fa', 
                            padding: '12px', 
                            borderRadius: '6px',
                            fontSize: '13px',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {renderFollowUpContent(item.content)}
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>
          </div>
        )}
      </Drawer>

      {/* 图片查看器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />
    </div>
    </main>
  );
};

export default FollowUpRecords; 